import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { RovoAgentIcon } from '@atlassian/conversation-assistant-ui-components';
import { UserInputCommandPalette } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';
import { useAnalyticsFlowEventsQueue } from '../../analytics/analytics-flow/analyticsFlowUtils';
import { createAgentConfigItem } from '../../prebuilt/config-item-agents/create-agent-config-item';
import { analyticsFormatMessage } from '../../utils/format-message';
import { useCommandPaletteStateMachine } from '../useCommandPaletteStateMachine';
import { messages } from './../messages';

/**
 * This component is responsible for the logic and rendering of the
 * Command Palette component, it also handles the corresponding state machine.
 */
export function UserInputCommandPaletteWithLogic({
  send: sendToAiExperience,
  endExperience,
  editorPluginAIProvider,
  context: experienceAppContext
}) {
  var _startingConfig$promp, _configItem$promptLab2, _configItem$promptHin;
  const {
    formatMessage
  } = useIntl();
  const {
    configItem: startingConfig,
    baseGenerate,
    userInput: startingUserInput,
    userADFInput: startingUserADFInput,
    editCount,
    positions,
    editorView,
    latestPromptResponse,
    agents
  } = experienceAppContext;
  const {
    context: commandPaletteContext,
    send: sendToCommandPalette
  } = useCommandPaletteStateMachine({
    baseGenerate,
    startingConfig,
    startingPresetPromptLabel: (_startingConfig$promp = startingConfig.promptLabel) === null || _startingConfig$promp === void 0 ? void 0 : _startingConfig$promp.call(startingConfig, {
      formatMessage
    }),
    startingUserInput,
    startingUserADFInput
  });
  const {
    textInput,
    adfInput,
    configItem,
    presetPromptLabel
  } = commandPaletteContext;
  const focusInputRef = React.useRef(undefined);
  const handleCancel = () => {
    const isInputEmpty = !commandPaletteContext.textInput ? true : commandPaletteContext.textInput.trim().length === 0;
    if (isInputEmpty) {
      endExperience();
    } else {
      sendToAiExperience({
        type: 'update context',
        context: {
          configItem,
          userInput: textInput || '',
          userADFInput: adfInput || ''
        }
      });
      sendToAiExperience({
        type: 'cancel'
      });
    }
  };
  const kickoffAiExperience = useCallback(({
    configItem,
    userInput,
    userADFInput
  }) => sendToAiExperience({
    type: 'kickoff with command palette input',
    context: {
      configItem,
      userInput,
      userADFInput
    }
  }), [sendToAiExperience]);
  const onPartialPresetSelected = useCallback(configItem => {
    var _configItem$promptLab, _focusInputRef$curren;
    // If partial prompt is selected we want to update the command palette
    // And focus the text input
    sendToCommandPalette({
      type: 'onPartialPresetSelected',
      configItem,
      presetPromptLabel: (_configItem$promptLab = configItem.promptLabel) === null || _configItem$promptLab === void 0 ? void 0 : _configItem$promptLab.call(configItem, {
        formatMessage
      })
    });
    (_focusInputRef$curren = focusInputRef.current) === null || _focusInputRef$curren === void 0 ? void 0 : _focusInputRef$curren.call(focusInputRef);
  }, [sendToCommandPalette, formatMessage]);
  const analyticsFlow = useAnalyticsFlowEventsQueue({
    stepName: 'prompt',
    attributes: {
      experienceName: configItem.title({
        formatMessage: analyticsFormatMessage
      }),
      promptTitle: (_configItem$promptLab2 = configItem.promptLabel) === null || _configItem$promptLab2 === void 0 ? void 0 : _configItem$promptLab2.call(configItem, {
        formatMessage: message => message.defaultMessage
      }),
      // !TODO AIFOLLOWUP - Remove this
      // This attribute only made sense when we had two different entry points
      // 'custom' or 'generate'
      // This should likely change to respresent the actual prompt types:
      // 'full-prompt' = when there is no user input (e.g. 'Summarize this page')
      // 'partial-prompt' = when there is user input and a prompt (e.g. 'Brainstorm: "Titles for this page"')
      // 'generate' = there is only user input, and no preset prompt
      promptType: 'generate',
      usingCommandPalette: true,
      editCount
    }
  });
  const updateAnalytics = useCallback(configItem => {
    var _configItem$getBacken;
    /**
     * Update the backendModel common attribute whenever the configItem changes
     */
    analyticsFlow.updateCommonAttributes({
      backendModel: configItem === null || configItem === void 0 ? void 0 : (_configItem$getBacken = configItem.getBackendModel) === null || _configItem$getBacken === void 0 ? void 0 : _configItem$getBacken.call(configItem, !!latestPromptResponse)
    });
    // update experiencename and title as it changes on selecting suggestion
    analyticsFlow.addAttributes({
      experienceName: configItem.title({
        formatMessage: analyticsFormatMessage
      }),
      promptTitle: (configItem.promptLabel || configItem.title)({
        formatMessage: analyticsFormatMessage
      })
    });
  }, [analyticsFlow, latestPromptResponse]);
  const updateTextInput = textInputValue => {
    sendToCommandPalette({
      type: 'onTextEntered',
      input: textInputValue
    });
  };
  const updateADFInput = React.useCallback(text => {
    sendToCommandPalette({
      type: 'onADFEntered',
      adf: text
    });
  }, [sendToCommandPalette]);
  const onSubmit = useCallback(() => {
    kickoffAiExperience({
      configItem: commandPaletteContext.configItem,
      userInput: commandPaletteContext.textInput || '',
      userADFInput: commandPaletteContext.adfInput || ''
    });
  }, [kickoffAiExperience, commandPaletteContext.configItem, commandPaletteContext.textInput, commandPaletteContext.adfInput]);
  const __getSuggestions = useMemo(() => {
    return configItem.selectionType === 'range' ? editorPluginAIProvider.rangeConfig.getSuggestions : editorPluginAIProvider.emptyConfig.getSuggestions;
  }, [configItem.selectionType, editorPluginAIProvider.emptyConfig.getSuggestions, editorPluginAIProvider.rangeConfig.getSuggestions]);

  /**
   * This function is responsible for highlighting the entire document
   * when particular prompts are selected.
   *
   * This only happens if the user has not already made a selection.
   *
   * Currently all prompts highlight the whole document except partial prompts like:
   * - Brainstorm
   * - Ask Search
   */
  const handleHighlight = useCallback(({
    configItem,
    promptLabel
  }) => {
    // Remove all decorations if the prompt is 'Tell AI' or that it is not defined (brainstorm)
    if (configItem.promptLabel !== undefined || promptLabel === 'tell-ai') {
      const removeTransaction = editorView.state.tr;
      removeTransaction.setMeta('hideSelectionPreview', true);
      editorView.dispatch(removeTransaction);
      return;
    }

    // Highlight the entire document for all other prompts
    const setTransaction = editorView.state.tr;
    setTransaction.setMeta('showSelectionPreview', true);
    editorView.dispatch(setTransaction);
  }, [editorView]);
  const getSuggestions = useCallback(inputValue => {
    return __getSuggestions({
      inputValue
    }).filter(configItem => {
      return configItem.isVisible({
        editorView,
        positions
      });
    }).map(configItem => {
      var _configItem$icon;
      return {
        icon: (_configItem$icon = configItem.icon) === null || _configItem$icon === void 0 ? void 0 : _configItem$icon.call(configItem, {
          formatMessage,
          shownAt: 'suggestions'
        }),
        label: configItem.title({
          formatMessage
        }),
        statusLozengeType: configItem.statusLozengeType,
        // Note -- we can probably delete value
        // appears to only be used internally for a react key
        // not appear to be getting used)
        value: configItem.title({
          formatMessage
        }),
        onSuggestionFocused: () => {
          handleHighlight({
            configItem
          });
        },
        onSelect: () => {
          var _configItem$promptLab3;
          handleHighlight({
            configItem
          });
          updateAnalytics(configItem);

          // If full prompt is selected we want to kickoff the AI experience
          if (!((_configItem$promptLab3 = configItem.promptLabel) !== null && _configItem$promptLab3 !== void 0 && _configItem$promptLab3.call(configItem, {
            formatMessage
          }))) {
            kickoffAiExperience({
              userInput: '',
              userADFInput: '',
              configItem
            });
          } else {
            onPartialPresetSelected(configItem);
          }
        }
      };
    }).filter(configItem => configItem.label.toLowerCase().includes((inputValue || '').toLowerCase())).concat(!getBooleanFF('platform.editor.ai-rovo-agents') ? [] : agents.map(agent => {
      const agentConfigItem = createAgentConfigItem(startingConfig.selectionType, agent);
      return {
        value: `agent:${agent.id}`,
        label: agent.name,
        description: agent.description,
        icon: /*#__PURE__*/React.createElement(RovoAgentIcon, {
          label: agent.name
        }),
        group: formatMessage(messages.agentsSuggestionGroupHeading),
        onSelect: () => {
          handleHighlight({
            configItem: agentConfigItem
          });
          updateAnalytics(agentConfigItem);
          onPartialPresetSelected(agentConfigItem);
        },
        onSuggestionFocused: () => {
          handleHighlight({
            configItem: agentConfigItem
            // promptLabel: agent.name,
          });
        }
      };
    })).concat(inputValue ? [{
      value: 'tell-ai',
      label: inputValue,
      onSelect: () => {
        handleHighlight({
          configItem
        });
        updateAnalytics(configItem);
        onSubmit();
      },
      onSuggestionFocused: () => {
        handleHighlight({
          configItem,
          promptLabel: 'tell-ai'
        });
      }
    }] : []);
  }, [configItem, __getSuggestions, onSubmit, formatMessage, editorView, positions, handleHighlight, kickoffAiExperience, agents, startingConfig, onPartialPresetSelected, updateAnalytics]);
  return /*#__PURE__*/React.createElement(UserInputCommandPalette, {
    presetPromptLabel: presetPromptLabel,
    inputValue: textInput || '',
    inputADFValue: adfInput,
    onInputUpdate: updateTextInput,
    onADFUpdate: updateADFInput,
    onCancel: handleCancel,
    onEscapeKeyPressed: handleCancel,
    onSubmit: onSubmit,
    onRefinementTagRemove: () => {
      var _baseGenerate$promptL, _baseGenerate$getBack, _focusInputRef$curren2;
      // Set experienceName and promptTitle to default values
      analyticsFlow.addAttributes({
        experienceName: baseGenerate === null || baseGenerate === void 0 ? void 0 : baseGenerate.title({
          formatMessage: message => message.defaultMessage
        }),
        promptTitle: baseGenerate === null || baseGenerate === void 0 ? void 0 : (_baseGenerate$promptL = baseGenerate.promptLabel) === null || _baseGenerate$promptL === void 0 ? void 0 : _baseGenerate$promptL.call(baseGenerate, {
          formatMessage: message => message.defaultMessage
        })
      });
      analyticsFlow.updateCommonAttributes({
        backendModel: baseGenerate === null || baseGenerate === void 0 ? void 0 : (_baseGenerate$getBack = baseGenerate.getBackendModel) === null || _baseGenerate$getBack === void 0 ? void 0 : _baseGenerate$getBack.call(baseGenerate, !!latestPromptResponse)
      });
      sendToCommandPalette({
        type: 'onPartialPresetRemoved'
      });
      (_focusInputRef$curren2 = focusInputRef.current) === null || _focusInputRef$curren2 === void 0 ? void 0 : _focusInputRef$curren2.call(focusInputRef);
    },
    placeholder: ((_configItem$promptHin = configItem.promptHint) === null || _configItem$promptHin === void 0 ? void 0 : _configItem$promptHin.call(configItem, {
      formatMessage
    })) || '',
    getSuggestions: getSuggestions,
    focusInputRef: focusInputRef,
    PromptEditor: editorPluginAIProvider.PromptEditor,
    agent: configItem.agent
  });
}